import { useLocation } from "react-router-dom";
import { Bell, ChevronsUpDown, CircleHelp, LifeBuoy, Loader2, MessageCircle } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/common/dropdown/DropDownV2";
import { useSupportChat } from "components/common/SupportButton";
import { UserAvatar } from "components/common/UserAvatar";
import { UserProfileMenuItems, useUserProfileMenu } from "components/common/UserProfileDropdown";
import { PATH_DASHBOARD } from "config";
import { helpArticleUrls } from "config/helpArticleUrls";
import { isNavPathActive } from "config/navigation";
import { useAppSidebarLayout } from "./appSidebarLayout";
import { CollapsedTooltip, SidebarIcon, SidebarRowButton, SidebarRowLink } from "./SidebarRow";
import { useTrackNavClick } from "./useTrackNavClick";

/** Unread count next to a Notifications row ("99+" above 99, from the menu). */
export const NotificationBadge = ({ count }: { count: string | number }) => (
  <span className="grid h-4 min-w-4 place-items-center rounded-full bg-notification px-1 text-[10px] font-semibold text-notification-foreground">
    {count}
  </span>
);

/** Help and support: the support chat and the help center. */
const HelpAndSupportRow = () => {
  const { collapsed } = useAppSidebarLayout();
  const { isOpen, isPending, toggleChat } = useSupportChat();
  const label = "Help and support";

  return (
    <DropdownMenu>
      <CollapsedTooltip label={label}>
        <DropdownMenuTrigger asChild>
          <SidebarRowButton icon={<SidebarIcon icon={CircleHelp} />} label={label} />
        </DropdownMenuTrigger>
      </CollapsedTooltip>
      <DropdownMenuContent
        side={collapsed ? "right" : "top"}
        align="start"
        sideOffset={6}
        className="w-56 rounded-lg"
      >
        <DropdownMenuItem onClick={toggleChat} disabled={isPending}>
          {isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <MessageCircle className="h-4 w-4" aria-hidden />
          )}
          {isOpen ? "Close support chat" : "Chat with support"}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => window.open(helpArticleUrls.home, "_blank")}>
          <LifeBuoy className="h-4 w-4" aria-hidden />
          Help center
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** The red dot on a collapsed Notifications icon when something is unread. */
export const NotificationDot = () => (
  <span aria-hidden className="absolute right-2.5 top-1.5 h-2 w-2 rounded-full bg-notification" />
);

/**
 * Bottom of every sidebar: help, notifications with unread count, account
 * menu. `showNotifications={false}` leaves Notifications out where the
 * sidebar's own navigation already lists it (personal settings), so the row
 * is never shown, or highlighted, twice.
 */
export const AppSidebarFooter = ({ showNotifications = true }: { showNotifications?: boolean }) => {
  const { collapsed } = useAppSidebarLayout();
  const { pathname } = useLocation();
  const trackNavClick = useTrackNavClick();
  const menu = useUserProfileMenu();
  const notificationsLabel = menu.hasUnread
    ? `Notifications, ${menu.unreadCount} unread`
    : "Notifications";

  return (
    <div className="flex flex-col gap-0.5">
      <HelpAndSupportRow />

      {showNotifications ? (
        <CollapsedTooltip label={notificationsLabel}>
          <SidebarRowLink
            to={PATH_DASHBOARD.notifications}
            icon={<SidebarIcon icon={Bell} />}
            label="Notifications"
            aria-label={notificationsLabel}
            active={isNavPathActive(pathname, PATH_DASHBOARD.notifications)}
            onClick={() => trackNavClick({ area: "footer", item: "notifications" })}
            trailing={menu.hasUnread ? <NotificationBadge count={menu.displayCount} /> : null}
            collapsedAdornment={menu.hasUnread ? <NotificationDot /> : null}
          />
        </CollapsedTooltip>
      ) : null}

      <DropdownMenu>
        <CollapsedTooltip label={menu.displayName || "Account"}>
          <DropdownMenuTrigger asChild>
            <SidebarRowButton
              aria-label={
                menu.displayName ? `Account menu for ${menu.displayName}` : "Account menu"
              }
              icon={
                <UserAvatar size="sm" className="profile-dropdown-avatar shrink-0 rounded-full" />
              }
              label={menu.displayName || "Account"}
              className="profile-dropdown-trigger"
              trailing={
                <ChevronsUpDown
                  className="h-4 w-4 shrink-0 text-chrome-muted-foreground"
                  aria-hidden
                />
              }
            />
          </DropdownMenuTrigger>
        </CollapsedTooltip>
        <DropdownMenuContent
          className="w-56 rounded-lg"
          side={collapsed ? "right" : "top"}
          align={collapsed ? "end" : "start"}
          sideOffset={6}
        >
          <UserProfileMenuItems menu={menu} />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
